import React, { useCallback, useEffect, useRef, useState } from "react";
import { balanceToneClass } from '../../lib/helpers/balance';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import apiFetch, { safeJson } from "../../context/api";

/**
 * PWA del SOCIO (CRUO F1) — canal propio, sin sidebar de staff.
 *
 * - Vinculación: el staff enseña un QR (Sala → "📱 Vincular móvil"); /app/vincular
 *   canjea el token y guarda la sesión del socio en este dispositivo.
 * - Pedir SOLO EN SALA (el backend devuelve 403 not_in_club si no hay entrada).
 * - Mesa por QR: /mesa/<token> fija la mesa — el pedido sale "servir en mesa".
 * - Modelo cartera: todo se paga con saldo; si queda en negativo, se ve en rojo.
 */

const TOKEN_KEY = "memberToken";
const TABLE_KEY = "memberTableId";
const TABLE_LABEL_KEY = "memberTableLabel";
const POLL_MS = 10000;

function eur(n) {
  return `${Number(n || 0).toFixed(2)} €`;
}

function useMemberApi() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const call = useCallback(
    async (path, options = {}) => {
      const res = await apiFetch(path, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      });
      return res;
    },
    [token]
  );
  const save = useCallback((t) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  }, []);
  const clear = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);
  return { token, call, save, clear };
}

// ── /app/vincular?token=… ────────────────────────────────────────────────────
export function MemberLink() {
  const location = useLocation();
  const navigate = useNavigate();
  const { save } = useMemberApi();
  const [error, setError] = useState("");

  useEffect(() => {
    const linkToken = new URLSearchParams(location.search).get("token");
    if (!linkToken) {
      setError("Falta el código del QR.");
      return;
    }
    (async () => {
      try {
        const res = await apiFetch(`/v1/member/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ link_token: linkToken }),
        });
        if (!res.ok) {
          const detail = (await res.json().catch(() => null))?.detail;
          throw new Error(typeof detail === "string" ? detail : "No se pudo vincular");
        }
        const data = await safeJson(res);
        save(data.access_token);
        navigate("/app", { replace: true });
      } catch (e) {
        setError(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--muted)] p-6">
      <div className="w-full max-w-sm rounded-2xl border border-[#E5E5E5] bg-white p-6 text-center shadow-sm">
        {error ? (
          <>
            <div className="text-3xl">😕</div>
            <p className="mt-2 text-sm text-[#DC2626]">{error}</p>
            <p className="mt-2 text-xs text-[#737373]">Pide al staff que genere un QR nuevo.</p>
          </>
        ) : (
          <p className="text-sm text-[#737373]">Vinculando tu móvil…</p>
        )}
      </div>
    </div>
  );
}

// ── /mesa/<qrToken> ──────────────────────────────────────────────────────────
export function MemberTable() {
  const { qrToken } = useParams();
  const navigate = useNavigate();
  const { token, call } = useMemberApi();
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      // Aún sin vincular: recordamos la mesa y mandamos a la pantalla de vincular.
      localStorage.setItem("pendingTableQr", qrToken || "");
      navigate("/app", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await call(`/v1/member/table/${qrToken}`);
        if (!res.ok) throw new Error("Mesa no encontrada — pide al staff");
        const mesa = await safeJson(res);
        localStorage.setItem(TABLE_KEY, mesa.id);
        localStorage.setItem(TABLE_LABEL_KEY, mesa.label || "");
        navigate("/app", { replace: true });
      } catch (e) {
        setError(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrToken, token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--muted)] p-6">
      <div className="w-full max-w-sm rounded-2xl border border-[#E5E5E5] bg-white p-6 text-center shadow-sm">
        {error ? <p className="text-sm text-[#DC2626]">{error}</p> : <p className="text-sm text-[#737373]">Fijando tu mesa…</p>}
      </div>
    </div>
  );
}

// ── /app — la app del socio ──────────────────────────────────────────────────
export default function MemberApp() {
  const { token, call, clear } = useMemberApi();
  const [me, setMe] = useState(null);
  const [products, setProducts] = useState([]);
  const [categoria, setCategoria] = useState("Todo");
  const [cart, setCart] = useState({ items: [], version: 0 });
  const [orders, setOrders] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const versionRef = useRef(0);
  versionRef.current = cart.version;
  const tableId = localStorage.getItem(TABLE_KEY) || null;
  const tableLabel = localStorage.getItem(TABLE_LABEL_KEY) || "";

  const cargar = useCallback(async () => {
    if (!token) return;
    try {
      const [meRes, prodRes, cartRes, ordRes] = await Promise.all([
        call(`/v1/member/me`),
        call(`/v1/member/products`),
        call(`/v1/member/cart`),
        call(`/v1/member/orders`),
      ]);
      if (meRes.status === 401) {
        clear();
        return;
      }
      if (meRes.ok) setMe(await safeJson(meRes));
      if (prodRes.ok) setProducts((await safeJson(prodRes)) || []);
      if (cartRes.ok) {
        const c = await safeJson(cartRes);
        setCart({ items: c?.items || [], version: c?.version || 0 });
      }
      if (ordRes.ok) setOrders((await safeJson(ordRes)) || []);
    } catch {
      /* siguiente poll */
    }
  }, [token, call, clear]);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, POLL_MS);
    return () => clearInterval(t);
  }, [cargar]);

  // Mesa escaneada ANTES de vincular: al llegar aquí ya con sesión, se resuelve.
  useEffect(() => {
    const pending = localStorage.getItem("pendingTableQr");
    if (!token || !pending) return;
    (async () => {
      try {
        const res = await call(`/v1/member/table/${pending}`);
        if (res.ok) {
          const mesa = await safeJson(res);
          localStorage.setItem(TABLE_KEY, mesa.id);
          localStorage.setItem(TABLE_LABEL_KEY, mesa.label || "");
        }
      } finally {
        localStorage.removeItem("pendingTableQr");
      }
    })();
  }, [token, call]);

  const saveCart = useCallback(
    async (items, { retry = true } = {}) => {
      setBusy(true);
      try {
        const res = await call(`/v1/member/cart`, {
          method: "POST",
          body: JSON.stringify({
            items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity, unit_price: it.unit_price })),
            base_version: versionRef.current,
            table_id: tableId,
          }),
        });
        if (res.status === 409 && retry) {
          const fresh = await call(`/v1/member/cart`);
          if (fresh.ok) {
            const c = await safeJson(fresh);
            setCart({ items: c?.items || [], version: c?.version || 0 });
            versionRef.current = c?.version || 0;
          }
          return saveCart(items, { retry: false });
        }
        if (!res.ok) throw new Error("No se pudo guardar el pedido");
        const data = await safeJson(res);
        setCart({ items: data?.items || [], version: data?.version || 0 });
        setMsg(null);
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      } finally {
        setBusy(false);
      }
    },
    [call, tableId]
  );

  const add = useCallback(
    (p) => {
      const items = [...cart.items];
      const i = items.findIndex((it) => it.product_id === p.id);
      if (i >= 0) items[i] = { ...items[i], quantity: Number(items[i].quantity) + 1 };
      // p.price YA llega resuelto por línea de coste desde el servidor
      // (/v1/member/products): no es la tarifa. No lo pases por
      // effectiveUnitPrice — doblaría la resolución con datos que la PWA no tiene.
      else items.push({ product_id: p.id, quantity: 1, unit_price: Number(p.price || 0), _name: p.name });
      saveCart(items);
    },
    [cart.items, saveCart]
  );

  const setQty = useCallback(
    (pid, qty) => {
      const items = cart.items
        .map((it) => (it.product_id === pid ? { ...it, quantity: qty } : it))
        .filter((it) => Number(it.quantity) > 0);
      saveCart(items);
    },
    [cart.items, saveCart]
  );

  const pedir = useCallback(async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await call(`/v1/member/cart/checkout`, {
        method: "POST",
        body: JSON.stringify({ base_version: versionRef.current }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null))?.detail;
        if (detail?.code === "not_in_club") throw new Error(detail.message || "Pide al llegar al club");
        throw new Error(typeof detail === "string" ? detail : "No se pudo enviar el pedido");
      }
      const order = await safeJson(res);
      setCart({ items: [], version: 0 });
      setMsg({
        tone: "ok",
        text: order?.fulfillment === "serve" ? `Pedido enviado 🍽️ — te lo llevamos a ${tableLabel || "tu mesa"}` : "Pedido enviado 🛎️ — te avisamos para recogerlo en barra",
      });
      cargar();
    } catch (e) {
      setMsg({ tone: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  }, [call, cargar, tableLabel]);

  // ── sin vincular ──
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--muted)] p-6">
        <div className="w-full max-w-sm rounded-2xl border border-[#E5E5E5] bg-white p-8 text-center shadow-sm">
          <div className="text-4xl">📱</div>
          <h1 className="mt-3 text-xl font-bold text-[#0A0A0A]">Tu club, en tu móvil</h1>
          <p className="mt-2 text-sm text-[#737373]">
            Pide al staff que te <b>vincule el móvil</b>: te enseñarán un QR — escanéalo y listo. Sin contraseñas.
          </p>
        </div>
      </div>
    );
  }

  const categorias = ["Todo", ...Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort()];
  const visibles = products.filter((p) => categoria === "Todo" || p.category === categoria);
  const total = cart.items.reduce((s, it) => s + Number(it.quantity) * Number(it.unit_price || 0), 0);
  const nameOf = (pid) => products.find((p) => p.id === pid)?.name || "Producto";
  const activos = orders.filter((o) => o.status === "PENDING" || o.status === "PREPARING" || o.status === "pending" || o.status === "preparing");

  return (
    <div className="min-h-screen bg-[var(--muted)] pb-40">
      {/* cabecera */}
      <header className="sticky top-0 z-10 border-b border-[#E5E5E5] bg-white px-4 py-3">
        <div className="mx-auto flex max-w-lg items-center justify-between">
          <div>
            <div className="text-sm font-bold text-[#0A0A0A]">Hola{me?.first_name ? `, ${me.first_name}` : ""} 👋</div>
            <div className="text-xs text-[#737373]">
              {me?.in_club ? (
                <span className="font-semibold text-[#16A34A]">● En sala</span>
              ) : (
                <span>Fuera del club</span>
              )}
              {tableLabel && <span> · 🍽️ {tableLabel}</span>}
            </div>
          </div>
          <div className={`text-right text-sm font-bold ${balanceToneClass(me?.credit_balance, "text-[var(--foreground)]")}`}>
            {eur(me?.credit_balance)}
            <div className="text-[10px] font-normal uppercase tracking-wide text-[#A3A3A3]">saldo</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4">
        {/* pedidos en curso */}
        {activos.length > 0 && (
          <div className="mt-3 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-2.5 text-sm text-cyan-900">
            {activos.length === 1 ? "Tu pedido está" : `${activos.length} pedidos están`} en marcha —{" "}
            {activos.some((o) => String(o.status).toLowerCase() === "preparing") ? "preparándose 🍹" : "en cola"}
          </div>
        )}

        {msg && (
          <div
            role="status"
            className={`mt-3 rounded-xl border px-4 py-2.5 text-sm ${
              msg.tone === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-700"
            }`}
          >
            {msg.text}
          </div>
        )}

        {/* categorías */}
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {categorias.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategoria(c)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm ${
                categoria === c ? "bg-[#0A0A0A] text-white" : "bg-white text-[#525252] border border-[#E5E5E5]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* productos */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          {visibles.map((p) => (
            <div key={p.id} className="flex flex-col rounded-xl border border-[#E5E5E5] bg-white p-2 shadow-sm">
              {p.image_path && (
                <div className="relative w-full overflow-hidden rounded-lg bg-[#F8FAFC]" style={{ paddingTop: "75%" }}>
                  <img src={p.image_path} alt={p.name} loading="lazy" className="absolute left-0 top-0 h-full w-full object-cover" />
                </div>
              )}
              <div className="mt-1.5 truncate text-sm font-medium text-[#0A0A0A]">{p.name}</div>
              <div className="text-xs text-[#737373]">{eur(p.price)}</div>
              <button
                type="button"
                disabled={busy}
                onClick={() => add(p)}
                className="mt-1.5 rounded-md bg-lime-500 py-1.5 text-xs font-semibold text-white active:scale-95 disabled:bg-[#E5E5E5]"
              >
                + Añadir
              </button>
            </div>
          ))}
        </div>
      </main>

      {/* carrito fijo abajo */}
      {cart.items.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[#E5E5E5] bg-white p-4 shadow-2xl">
          <div className="mx-auto max-w-lg">
            <ul className="max-h-36 space-y-1.5 overflow-y-auto">
              {cart.items.map((it) => (
                <li key={it.product_id} className="flex items-center justify-between text-sm">
                  <span className="min-w-0 flex-1 truncate">{it._name || nameOf(it.product_id)}</span>
                  <span className="flex items-center gap-1.5">
                    <button type="button" disabled={busy} onClick={() => setQty(it.product_id, Number(it.quantity) - 1)} className="h-7 w-7 rounded border border-[#E5E5E5] text-[#525252]">−</button>
                    <b className="w-5 text-center">{Number(it.quantity)}</b>
                    <button type="button" disabled={busy} onClick={() => setQty(it.product_id, Number(it.quantity) + 1)} className="h-7 w-7 rounded border border-[#E5E5E5] text-[#525252]">+</button>
                  </span>
                  <span className="w-14 text-right text-[#737373]">{eur(Number(it.quantity) * Number(it.unit_price || 0))}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              disabled={busy}
              onClick={pedir}
              className="mt-3 w-full rounded-xl bg-[#0A0A0A] py-3 text-sm font-bold text-white active:scale-[0.99] disabled:bg-[#E5E5E5] disabled:text-[#A3A3A3]"
            >
              {busy ? "Enviando…" : `Pedir · ${eur(total)}${tableLabel ? ` · 🍽️ ${tableLabel}` : " · 🛎️ recoger en barra"}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
